// ===========================================
// Teacher Tasks Page (Teacher View)
// ===========================================
// View all school classes with attendance and notes functionality
// Teachers can view their own or all classes for coverage purposes

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  Divider,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { format, addDays, startOfWeek, differenceInHours, parse } from 'date-fns';
import PageHeader from '../../components/common/PageHeader';
import LessonAttendanceButton from '../../components/attendance/LessonAttendanceButton';
import LessonNotesButton from '../../components/notes/LessonNotesButton';
import LessonPiecesButton from '../../components/gamification/LessonPiecesButton';
import { useAuth } from '../../contexts/useAuth';
import { useLessons, useLessonOccurrences } from '../../hooks/useLessons';
import { useTeachers } from '../../hooks/useUsers';
import { useEnrolledStudentsForAttendance } from '../../hooks/useAttendance';
import { useLessonNoteCompletion, useNotesByLesson } from '../../hooks/useNotes';
import { useHybridIndividualSessionsThisWeek } from '../../hooks/useHybridBooking';
import { Lesson, HybridPattern, LessonOccurrence, getLessonTypeColor } from '../../api/lessons.api';
import { HybridSessionTaskRow } from '../../api/hybridBooking.api';
import {
  currentWeekNumber,
  toLocalDateString,
  parseUTCDate,
  formatUTCDate,
  localDateFromKey,
  termWeekCount,
  weekInLessonWindow,
} from '../../utils/dateUtils';
import { classRunsInWeek, ownClassWeeksRow } from '../../utils/classWeeks';
import RosterStudentPill from '../../components/attendance/RosterStudentPill';
import { enrollmentsForOccurrence } from '../../utils/occurrenceRoster';
import { birthdayThisWeek } from '../../utils/birthdays';

// ===========================================
// CONSTANTS
// ===========================================

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ===========================================
// HELPERS
// ===========================================

/**
 * Get the date for a lesson this week based on its day of week
 * Returns the date string formatted as "Mon, Jan 6"
 */
function getLessonDateThisWeek(dayOfWeek: number): string {
  const lessonDate = getLessonDateObjectThisWeek(dayOfWeek);
  return format(lessonDate, 'd MMM');
}

/**
 * Build a LOCAL-midnight Date from a `YYYY-MM-DD` occurrence key.
 *
 * NOT `parseUTCDate`. Every consumer of the returned Date formats it with
 * date-fns `format()`, which reads LOCAL getters — so a UTC-midnight Date
 * renders as the PREVIOUS day anywhere west of UTC. That made the date we
 * READ attendance for (the raw `dateKey`) differ from the date we WROTE it
 * to (the formatted Date): a teacher in New York marked attendance and the
 * button never flipped to "marked", because the write landed a day earlier
 * than the read. Invisible on the UTC+10 dev box, and invisible to the tests
 * (the button mock captured `toISOString()`, i.e. UTC, not what production
 * formats).
 *
 * This is the same bug as C3 on the teacher dashboard, which grew its own
 * copy of this helper; the fix was never back-ported here. Both copies were
 * hoisted into `utils/dateUtils` on 2026-08-19 so there is ONE implementation
 * — the duplication was the mechanism behind three separate shipped bugs.
 */

/**
 * Position of a JS day-of-week (0=Sun…6=Sat) within a Mon–Sun week: Mon=0 … Sun=6.
 * The app's weeks are MONDAY-anchored everywhere (backend notes completion,
 * term week numbering) — Sunday is the LAST day of a week, not the first.
 */
function mondayIndex(dayOfWeek: number): number {
  return (dayOfWeek + 6) % 7;
}

/**
 * Get the Date object for a lesson this week based on its day of week.
 * Monday-start week to match the backend's notes-completion weeks — the
 * old Sunday-start version keyed Sunday lessons (and every lesson when
 * viewed ON a Sunday) to a date one week off the backend's tracking.
 */
function getLessonDateObjectThisWeek(dayOfWeek: number): Date {
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday
  return addDays(weekStart, mondayIndex(dayOfWeek));
}

/**
 * Groups lessons by their core properties (name, teacher, room, day, time)
 * to deduplicate lessons that span multiple terms
 */
function deduplicateLessons(lessons: Lesson[]): Lesson[] {
  const seen = new Map<string, Lesson>();

  for (const lesson of lessons) {
    // Create a key based on properties that define a "same" lesson
    const key = `${lesson.name}-${lesson.teacherId}-${lesson.roomId}-${lesson.dayOfWeek}-${lesson.startTime}-${lesson.endTime}`;

    // Keep the first occurrence (which should have accurate _count)
    if (!seen.has(key)) {
      seen.set(key, lesson);
    }
  }

  return Array.from(seen.values());
}

/**
 * Compute the Monday-start calendar week containing `today`, formatted as
 * YYYY-MM-DD strings for the hybrid sessions hook.
 *
 * We format using local-tz components (`getFullYear/getMonth/getDate`) because
 * that's the calendar day the user is seeing. The backend stores
 * `scheduledDate` as UTC midnight of the school's local day; when the backend
 * parses our `2026-05-11` string via `new Date()`, it produces UTC midnight
 * 2026-05-11 — so the comparison agrees in any timezone the school is in.
 */
function getCurrentWeekRange(today: Date = new Date()): { weekStart: string; weekEnd: string } {
  const monday = startOfWeek(today, { weekStartsOn: 1 });
  const sunday = addDays(monday, 6);
  const toDateKey = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { weekStart: toDateKey(monday), weekEnd: toDateKey(sunday) };
}

/**
 * Discriminated-union row descriptor so the visible list can interleave parent
 * lesson rows and per-booking hybrid session rows, sorted together by
 * (dayOfWeek, startTime).
 */
type RowDescriptor =
  | { kind: 'lesson'; lesson: Lesson; sortKey: { dayOfWeek: number; startTime: string } }
  | { kind: 'session'; session: HybridSessionTaskRow; sortKey: { dayOfWeek: number; startTime: string } };

/**
 * Returns true when `lesson` actually runs in the current week.
 *
 * Rules:
 * - HYBRID parent rows: only run on the "primary" weeks of their combination
 *   (group weeks for GROUP_INDIVIDUAL/GROUP_BAND, individual weeks for
 *   INDIVIDUAL_BAND). Band weeks of a hybrid course are surfaced as a
 *   separate paired BAND lesson row — never on the parent slot.
 * - INDIVIDUAL / GROUP / BAND rows: every week of the class window, unless their own-term
 *   LessonTermWeeks row is CUSTOM (a band's rehearsals, a fortnightly class) — read through
 *   the one rule, utils/classWeeks.ts. A BAND with NO own row falls back to the partner
 *   HYBRID pattern's bandWeeks if one references this lesson via bandLessonId.
 * - Every row: not before `startWeek` nor after `endWeek` (checked first).
 *
 * Out-of-term lessons return true so the existing term filter at the call
 * site (which drops them) stays the sole authority on term boundaries.
 */
function lessonRunsInWeekFE(
  lesson: Lesson,
  /**
   * The Monday (UTC-midnight Date) of the week the page is DISPLAYING. Never
   * `new Date()`: a UTC instant is still LAST week until 10:00 Sydney on a Monday,
   * so a class starting that week was hidden (and one that ended the week before
   * still listed with an attendance button) all Monday morning. The page's own
   * week range is local-Monday-anchored, so the filter and the page agree.
   */
  weekMonday: Date,
  partnerPatternsByBandLessonId: Map<string, HybridPattern>,
): boolean {
  if (!lesson.Term?.startDate || !lesson.Term?.endDate) return true;
  const weekSunday = new Date(weekMonday.getTime() + 6 * 86_400_000);
  if (weekSunday.getTime() < new Date(lesson.Term.startDate).getTime()) return true;
  if (weekMonday.getTime() > new Date(lesson.Term.endDate).getTime()) return true;

  const wk = currentWeekNumber(lesson.Term.startDate, weekMonday);
  const type = lesson.LessonType.type;

  // The CLASS window (mid-term start / last week) comes FIRST, before any type branch:
  // INDIVIDUAL / GROUP / every-week BAND classes have no week list, so this is the only
  // thing that hides a week before they start or after they end.
  if (!weekInLessonWindow(wk, lesson, termWeekCount(lesson.Term.startDate, lesson.Term.endDate))) {
    return false;
  }

  if (type === 'HYBRID') {
    const pattern = lesson.HybridLessonPattern ?? lesson.HybridPattern;
    if (!pattern) return true;
    switch (pattern.hybridCombination) {
      case 'GROUP_INDIVIDUAL':
      case 'GROUP_BAND':
        return (pattern.groupWeeks ?? []).includes(wk);
      case 'INDIVIDUAL_BAND':
        return (pattern.individualWeeks ?? []).includes(wk);
      default:
        return true;
    }
  }

  // A plain class: its stored weeks through the ONE rule. `?? null` is deliberate — not every
  // lessons endpoint sends the rows, and a class without them runs every week.
  const source = { ...lesson, LessonType: lesson.LessonType ?? null, LessonTermWeeks: lesson.LessonTermWeeks ?? null };
  // The partner-band fallback applies ONLY when the band has no row of its own: an EVERY_WEEK
  // row means every week, not "ask the partner" (Phase 2 /qa F2).
  if (type === 'BAND' && ownClassWeeksRow(source) === null) {
    const partner = partnerPatternsByBandLessonId.get(lesson.id);
    if (partner) {
      return (partner.bandWeeks ?? []).includes(wk);
    }
  }
  return classRunsInWeek(source, wk, termWeekCount(lesson.Term.startDate, lesson.Term.endDate));
}

// ===========================================
// LESSON ROW COMPONENT
// ===========================================

interface LessonRowProps {
  lesson: Lesson;
  viewMode: 'mine' | 'all';
  hideCompleted: boolean;
  /**
   * The resolved OCCURRENCE for this lesson this week, when the server has one.
   * Supplies the real date — which differs from the recurring `dayOfWeek` date
   * whenever the occurrence has been rescheduled — plus its cancelled state.
   * Absent only if the occurrence endpoint hasn't resolved yet, in which case we
   * fall back to the recurring date so the row still renders.
   */
  occurrence?: LessonOccurrence;
}

function LessonRow({ lesson, viewMode, hideCompleted, occurrence }: LessonRowProps) {
  // Prefer the occurrence's real date. Marking/reading attendance against the
  // recurring date after a reschedule is what made attendance look lost.
  const lessonDate = occurrence
    ? localDateFromKey(occurrence.dateKey)
    : getLessonDateObjectThisWeek(lesson.dayOfWeek ?? 0);
  const dateStr = occurrence?.dateKey ?? format(lessonDate, 'yyyy-MM-dd');
  const isCancelled = occurrence?.isCancelled ?? false;
  // Mon–Sun ordering: a Sunday lesson is the LAST of the week (the old raw
  // getDay() compare made every lesson "future" when viewed on a Sunday).
  //
  // Judged on the OCCURRENCE's weekday, not the recurring one. A class pulled
  // BACKWARD (Friday → Monday, the common direction for catch-ups) would
  // otherwise render at 45% opacity with pointerEvents:'none' — leaving the
  // teacher unable to click attendance for a class that already ran, and with
  // isOverdue forced false so it never surfaces as outstanding either.
  const effectiveDayOfWeek = occurrence
    ? localDateFromKey(occurrence.dateKey).getDay()
    : lesson.dayOfWeek ?? 0;
  const isFutureLesson = mondayIndex(effectiveDayOfWeek) > mondayIndex(new Date().getDay());

  const { data: attendanceData } = useEnrolledStudentsForAttendance(lesson.id, dateStr);
  const { data: noteCompletion } = useLessonNoteCompletion(lesson.id, dateStr);

  const students = attendanceData?.students || [];
  // Only students who have STARTED by this occurrence's week (mid-term start week).
  const rosterEnrollments = enrollmentsForOccurrence(lesson, lesson.LessonEnrollment, occurrence?.weekNumber);
  const hasAttendance = students.some((s) => s.attendance);
  const isNotesComplete = noteCompletion?.status === 'COMPLETE';
  const isComplete = hasAttendance && isNotesComplete;

  // Calculate overdue hours (from lesson end time)
  const now = new Date();
  const lessonEndTime = parse(lesson.endTime ?? '23:59', 'HH:mm', lessonDate);
  const hoursSinceEnd = differenceInHours(now, lessonEndTime);
  const isOverdue = !isFutureLesson && !isComplete && hoursSinceEnd >= 24;
  const isCriticallyOverdue = isOverdue && hoursSinceEnd >= 72;

  // Hide if both attendance and notes are completed
  if (hideCompleted && isComplete) {
    return null;
  }

  return (
    <ListItem
      // Surfaced for tests: `isFutureLesson` only drives styling (opacity +
      // pointerEvents), which MUI applies via a generated class, so there is no
      // other observable signal that a row has been dimmed and made unclickable.
      data-future={isFutureLesson ? 'true' : 'false'}
      sx={{
        border: isOverdue ? (isCriticallyOverdue ? 3 : 2) : 1,
        borderColor: isOverdue ? '#FFAE9E' : 'divider',
        borderRadius: 1,
        mb: 1,
        '&:last-child': { mb: 0 },
        ...(isFutureLesson && { opacity: 0.45, pointerEvents: 'none' }),
        // A cancelled occurrence stays visible but inert — a teacher expecting
        // the class must see it was cancelled rather than find it missing.
        ...(isCancelled && { opacity: 0.6, borderColor: 'error.main' }),
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="subtitle1"
              fontWeight="medium"
              sx={isCancelled ? { textDecoration: 'line-through' } : undefined}
            >
              {lesson.name}
            </Typography>
            {isCancelled && <Chip label="Cancelled" size="small" color="error" />}
            {occurrence?.isRescheduled && (
              <Chip label="Rescheduled" size="small" color="warning" variant="outlined" />
            )}
            <Chip
              label={lesson.LessonType?.name || 'Unknown'}
              size="small"
              color={lesson.LessonType?.color ? undefined : getLessonTypeColor(lesson.LessonType?.type || '')}
              sx={lesson.LessonType?.color ? {
                backgroundColor: lesson.LessonType.color,
                color: '#fff',
              } : undefined}
            />
          </Box>
        }
        secondary={
          <Box component="span" sx={{ display: 'block' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
              {/* Show the occurrence's REAL date. Using the recurring dayOfWeek
                  here would label a rescheduled class with the day it no longer
                  runs on. */}
              <Chip
                label={
                  occurrence
                    ? formatUTCDate(parseUTCDate(occurrence.dateKey), {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'short',
                      })
                    : lesson.dayOfWeek != null
                      ? `${dayNames[lesson.dayOfWeek]}, ${getLessonDateThisWeek(lesson.dayOfWeek)}`
                      : 'TBD'
                }
                size="small"
                color={
                  (occurrence?.dateKey ?? '') ===
                  format(new Date(), 'yyyy-MM-dd')
                    ? 'primary'
                    : lesson.dayOfWeek === new Date().getDay() && !occurrence
                      ? 'primary'
                      : 'default'
                }
              />
              <Chip
                icon={<ScheduleIcon />}
                label={`${lesson.startTime} - ${lesson.endTime}`}
                size="small"
                variant="outlined"
              />
              {viewMode === 'all' && (
                <Chip
                  label={lesson.Instructor?.User?.firstName + ' ' + lesson.Instructor?.User?.lastName}
                  size="small"
                  variant={lesson.Instructor?.color ? 'filled' : 'outlined'}
                  icon={<PersonIcon />}
                  sx={
                    lesson.Instructor?.color
                      ? {
                          backgroundColor: lesson.Instructor.color,
                          color: '#fff',
                          '& .MuiChip-icon': { color: '#fff' },
                        }
                      : undefined
                  }
                />
              )}
              <Chip
                label={lesson.Room?.name}
                size="small"
                variant="outlined"
              />
            </Stack>
            {!!rosterEnrollments.length && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }} flexWrap="wrap">
                {rosterEnrollments.map((enrollment) => (
                  <RosterStudentPill
                    key={enrollment.Student.id}
                    student={enrollment.Student}
                    // The row already fetches per-student status — use it instead of
                    // reducing it to a boolean (2026-09-07).
                    status={students.find((s) => s.id === enrollment.Student.id)}
                    dateKey={dateStr}
                    birthday={birthdayThisWeek(enrollment.Student.birthDate, dateStr)}
                  />
                ))}
              </Stack>
            )}
          </Box>
        }
        primaryTypographyProps={{ component: 'div' }}
        secondaryTypographyProps={{ component: 'div' }}
      />
      {/* A cancelled occurrence gets no action buttons — the backend refuses
          attendance for one (409), so offering the button would be a dead end. */}
      {!isCancelled && (
        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
          {/* `lessonDate` is the OCCURRENCE date, so marking follows a
              rescheduled class instead of writing to the date it left. */}
          <LessonAttendanceButton
            lessonId={lesson.id}
            lessonName={lesson.name}
            date={lessonDate}
          />
          <LessonNotesButton
            lessonId={lesson.id}
            lessonName={lesson.name}
            date={lessonDate}
          />
          <LessonPiecesButton
            lessonId={lesson.id}
            lessonName={lesson.name}
          />
        </Box>
      )}
    </ListItem>
  );
}

// ===========================================
// HYBRID SESSION ROW COMPONENT
// ===========================================
//
// Renders one HybridBooking from a GROUP_INDIVIDUAL hybrid lesson on an
// individual week. Same flat-row layout as LessonRow but the date/time and
// student come from the booking, not the parent lesson, and the action
// buttons are scoped to the booked student so attendance and notes only
// involve that one student.

interface HybridSessionRowProps {
  session: HybridSessionTaskRow;
  viewMode: 'mine' | 'all';
  hideCompleted: boolean;
}

function HybridSessionRow({ session, viewMode, hideCompleted }: HybridSessionRowProps) {
  // LOCAL midnight, not UTC. `dateStr` below and the attendance button both
  // format this with local getters, so a UTC-midnight Date shifts BOTH the read
  // key and the write key back a day west of UTC — self-consistent, and
  // consistently wrong (clinic attendance filed against the previous day).
  //
  // NOTE: unlike `occurrence.dateKey`, `scheduledDate` arrives as a FULL ISO
  // timestamp (the backend passes the Date straight through — see
  // `getTeacherTaskSessions`). The shared `localDateFromKey` accepts either
  // shape: it takes the UTC calendar day off the instant, then rebuilds it at
  // local midnight — which is exactly what this needs.
  const sessionDate = useMemo(
    () => localDateFromKey(session.scheduledDate),
    [session.scheduledDate]
  );
  const dateStr = format(sessionDate, 'yyyy-MM-dd');
  const todayDow = new Date().getDay();
  const isFutureSession = session.dayOfWeek > todayDow;

  // Attendance for the single booked student.
  const { data: attendanceData } = useEnrolledStudentsForAttendance(
    session.lessonId,
    dateStr,
    session.Student.id
  );
  // Notes for this (lessonId, date) — scope to the booked student so we don't
  // count another concurrent booking's notes as "this session is done".
  const { data: notes } = useNotesByLesson(session.lessonId, { date: dateStr });

  const hasAttendance = (attendanceData?.students ?? []).some((s) => s.attendance);
  // "Complete" per user spec: attendance marked + at least one note of any
  // visibility exists for this student on this date.
  const hasAnyNote = (notes ?? []).some((n) => n.studentId === session.Student.id);
  const isComplete = hasAttendance && hasAnyNote;

  // Overdue logic mirrors LessonRow but uses the booking's own end time.
  const now = new Date();
  const sessionEndTime = parse(session.endTime, 'HH:mm', sessionDate);
  const hoursSinceEnd = differenceInHours(now, sessionEndTime);
  const isOverdue = !isFutureSession && !isComplete && hoursSinceEnd >= 24;
  const isCriticallyOverdue = isOverdue && hoursSinceEnd >= 72;

  if (hideCompleted && isComplete) {
    return null;
  }

  // Per-student status for the booked student (absence covers the booking's date — D5).
  const bookedStudentStatus = (attendanceData?.students ?? []).find((s) => s.id === session.Student.id);
return (
    <ListItem
      sx={{
        border: isOverdue ? (isCriticallyOverdue ? 3 : 2) : 1,
        borderColor: isOverdue ? '#FFAE9E' : 'divider',
        borderRadius: 1,
        mb: 1,
        '&:last-child': { mb: 0 },
        ...(isFutureSession && { opacity: 0.45, pointerEvents: 'none' }),
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {session.lessonName}
            </Typography>
            <Chip
              label={session.LessonType?.name || 'Hybrid'}
              size="small"
              color={session.LessonType?.color ? undefined : getLessonTypeColor('HYBRID')}
              sx={
                session.LessonType?.color
                  ? { backgroundColor: session.LessonType.color, color: '#fff' }
                  : undefined
              }
            />
            <Chip
              label="1:1 Clinic"
              size="small"
              sx={{ bgcolor: 'rgba(255,174,158,0.18)', color: '#c46b56', fontWeight: 600 }}
            />
          </Box>
        }
        secondary={
          <Box component="span" sx={{ display: 'block' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
              <Chip
                label={`${dayNames[session.dayOfWeek]}, ${format(sessionDate, 'd MMM')}`}
                size="small"
                color={session.dayOfWeek === todayDow ? 'primary' : 'default'}
              />
              <Chip
                icon={<ScheduleIcon />}
                label={`${session.startTime} - ${session.endTime}`}
                size="small"
                variant="outlined"
              />
              {viewMode === 'all' && (
                <Chip
                  label={`${session.Instructor.User.firstName} ${session.Instructor.User.lastName}`}
                  size="small"
                  variant={session.Instructor.color ? 'filled' : 'outlined'}
                  icon={<PersonIcon />}
                  sx={
                    session.Instructor.color
                      ? {
                          backgroundColor: session.Instructor.color,
                          color: '#fff',
                          '& .MuiChip-icon': { color: '#fff' },
                        }
                      : undefined
                  }
                />
              )}
              <Chip label={session.Room.name} size="small" variant="outlined" />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }} flexWrap="wrap">
              <RosterStudentPill
                student={session.Student}
                status={bookedStudentStatus}
                dateKey={dateStr}
                birthday={birthdayThisWeek(session.Student.birthDate, dateStr)}
              />
            </Stack>
          </Box>
        }
        primaryTypographyProps={{ component: 'div' }}
        secondaryTypographyProps={{ component: 'div' }}
      />
      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
        <LessonAttendanceButton
          lessonId={session.lessonId}
          lessonName={session.lessonName}
          date={sessionDate}
          studentId={session.Student.id}
        />
        <LessonNotesButton
          lessonId={session.lessonId}
          lessonName={session.lessonName}
          date={sessionDate}
          hideClassNotesTab
          studentId={session.Student.id}
        />
        <LessonPiecesButton
          lessonId={session.lessonId}
          lessonName={session.lessonName}
        />
      </Box>
    </ListItem>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function NotesAttendancePage() {
  const { user } = useAuth();

  // Filter state
  const [selectedDay, setSelectedDay] = useState<number | 'all'>('all');
  const [viewMode, setViewMode] = useState<'mine' | 'all'>('mine');
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>('');
  const [hideCompleted, setHideCompleted] = useState(true);

  // Fetch teachers list (only needed for "All School" view filter dropdown)
  const { data: teachers } = useTeachers();

  // Queries - fetch all lessons (we'll filter client-side).
  // When viewing "All School", a teacher needs to see every teacher's lessons.
  // The backend defaults to self-scope for TEACHER role unless allClasses is set.
  // "My Classes" asks for `mine`: a caller holding scheduling.read (an ADMIN or a staff
  // role) would otherwise get the whole school (roles R12, slice 1b §5.12).
  const { data: allLessons, isLoading: isLoadingLessons } = useLessons(
    viewMode === 'all' ? { allClasses: true } : { mine: true }
  );

  // Hybrid GROUP_INDIVIDUAL individual-week sessions for the current Monday→Sun
  // calendar week. Empty array while loading; replaces suppressed parent rows.
  //
  // todayKey is the local YYYY-MM-DD, refreshed once a minute so a teacher
  // leaving this page open across midnight automatically rolls into the new
  // week (instead of staring at yesterday's data).
  const [todayKey, setTodayKey] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  });
  useEffect(() => {
    const interval = setInterval(() => {
      const d = new Date();
      const next = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      setTodayKey((prev) => (prev === next ? prev : next));
    }, 60_000);
    return () => clearInterval(interval);
  }, []);
  const weekRange = useMemo(() => getCurrentWeekRange(), [todayKey]);
  const { data: hybridSessions, isLoading: isLoadingSessions } =
    useHybridIndividualSessionsThisWeek(weekRange);

  // Dated occurrences for this week, with LessonException applied. This is what
  // makes a rescheduled class appear on its NEW date (and attendance read/write
  // against that date) instead of the recurring dayOfWeek slot it has left.
  // MUST mirror the `allClasses` scope of the lessons query above. The route
  // force-scopes a TEACHER caller to their own teacherId unless allClasses is
  // set, so in "All School" mode without this every OTHER teacher's row would
  // get no occurrence and fall back to the recurring date permanently — the
  // original bug, for exactly the person covering a rescheduled class.
  const { data: occurrences } = useLessonOccurrences({
    startDate: weekRange.weekStart,
    endDate: weekRange.weekEnd,
    ...(viewMode === 'all' ? { allClasses: true } : { mine: true }),
  });

  /**
   * lessonId → this week's occurrence.
   *
   * One entry per lesson: a lesson meets at most once in a Mon–Sun week, so a
   * plain map is sufficient here. (If a week ever contained two occurrences of
   * one lesson — e.g. another was rescheduled INTO this week — the later one
   * wins; the row list is keyed by lesson, so it can only show one anyway.)
   */
  const occurrenceByLessonId = useMemo(() => {
    const map = new Map<string, LessonOccurrence>();
    for (const occ of occurrences ?? []) map.set(occ.lessonId, occ);
    return map;
  }, [occurrences]);

  const isLoading = isLoadingLessons || isLoadingSessions;

  // Find the current user's teacher record ID for filtering
  const currentTeacherId = useMemo(() => {
    if (!user || !teachers?.length) return null;
    const teacherRecord = teachers.find((t) => t.User?.id === user.id);
    return teacherRecord?.id || null;
  }, [user, teachers]);

  // Compose the visible row list: lessons swapped for sessions on
  // GROUP_INDIVIDUAL individual weeks, sorted by (dayOfWeek, startTime).
  const filteredRows = useMemo<RowDescriptor[]>(() => {
    if (!allLessons) return [];
    // "Today" as the page sees it (LOCAL calendar day, the same clock as `weekRange`),
    // as a UTC-midnight so it compares with the @db.Date term bounds day-to-day.
    // An instant compare left a term's first Monday empty until 10:00 Sydney.
    const todayDay = parseUTCDate(toLocalDateString(new Date())).getTime();
    const weekMonday = parseUTCDate(weekRange.weekStart);

    // Step 1: filter lessons by activity/schedule + teacher-scope, then drop
    // any whose Term doesn't cover today. Identical lessons split across terms
    // must resolve to the current term's row, otherwise the hybrid-week swap
    // below would see an out-of-term twin and let the parent row render through
    // on what should be an individual week.
    let lessons = allLessons.filter((l) => l.isActive && l.hasSchedule && l.dayOfWeek != null);
    if (viewMode === 'mine' && currentTeacherId) {
      lessons = lessons.filter((l) => l.teacherId === currentTeacherId);
    } else if (viewMode === 'all' && selectedTeacherId) {
      lessons = lessons.filter((l) => l.teacherId === selectedTeacherId);
    }
    lessons = lessons.filter((l) => {
      if (!l.Term?.startDate || !l.Term?.endDate) return true;
      const startMs = parseUTCDate(l.Term.startDate).getTime();
      const endMs = parseUTCDate(l.Term.endDate).getTime();
      return todayDay >= startMs && todayDay <= endMs;
    });

    // Step 2: drop lessons that don't actually run this week. Hidden cases:
    //   - Any class outside its own week window (mid-term start / last week).
    //   - HYBRID parent rows on weeks where the parent slot doesn't meet
    //     (e.g. GROUP_INDIVIDUAL individual weeks → swapped for HybridBooking
    //     session rows from the hook below; INDIVIDUAL_BAND band weeks →
    //     student is at the paired band rehearsal).
    //   - Paired BAND rows on weeks where the partner hybrid isn't in a
    //     band week (e.g. INDIVIDUAL_BAND on an individual week).
    //   - Standalone BAND rows on non-rehearsal weeks (BandTerm CUSTOM).
    // Build a band-lesson-id → partner-pattern map so paired BAND rows can
    // consult their hybrid partner's bandWeeks when they have no own BandTerm.
    // Last-write-wins on duplicate keys (identical bandWeeks in a sane school).
    const partnerPatternsByBandLessonId = new Map<string, HybridPattern>();
    for (const l of lessons) {
      const pattern = l.HybridLessonPattern ?? l.HybridPattern;
      if (pattern?.bandLessonId) {
        partnerPatternsByBandLessonId.set(pattern.bandLessonId, pattern);
      }
    }

    // This MUST run BEFORE de-duplication. The dedupe key (name/teacher/room/day/time)
    // is blind to the week window, so a class legitimately split in two ("Piano 1:1"
    // weeks 1-3 and a second one from week 4) collapsed to whichever row the API sent
    // first — and if that row didn't run this week, the class vanished. Deciding
    // "runs this week" first makes the result independent of API order.
    lessons = lessons.filter((lesson) => {
      // The SERVER's occurrence for the displayed week is authoritative: it already
      // applied the window, so a session MOVED INTO this week from a week the class
      // runs (endWeek 3, week-3 session rescheduled to week 4) is shown. Only kinds
      // this row hosts count: hybrid clinic and band-away weeks are emitted too, and
      // those stay governed by the swap rules below.
      const occ = occurrenceByLessonId.get(lesson.id);
      if (occ && (occ.kind === 'REGULAR' || occ.kind === 'HYBRID_GROUP')) return true;
      return lessonRunsInWeekFE(lesson, weekMonday, partnerPatternsByBandLessonId);
    });
    lessons = deduplicateLessons(lessons);

    const lessonRows: RowDescriptor[] = lessons
      .map((lesson) => {
        // Sort/filter on the OCCURRENCE's weekday, not the recurring template's.
        // The row body already renders the occurrence day (`effectiveDayOfWeek`),
        // so keying this off `lesson.dayOfWeek` put a class moved Fri→Tue under
        // the FRIDAY tab while labelling it "Tuesday" — filing it under the day
        // it no longer runs, which is the exact bug this sprint exists to kill.
        const occ = occurrenceByLessonId.get(lesson.id);
        return {
          kind: 'lesson' as const,
          lesson,
          sortKey: {
            dayOfWeek: occ ? localDateFromKey(occ.dateKey).getDay() : lesson.dayOfWeek ?? 0,
            startTime: occ?.startTime ?? lesson.startTime ?? '',
          },
        };
      });

    // Step 3: produce session rows from the hook. Already filtered server-side
    // to schoolId + GROUP_INDIVIDUAL + active statuses + this week. Apply the
    // same view-mode + day-tab filters that lessons get.
    const sessionsScoped = (hybridSessions ?? []).filter((s) => {
      if (viewMode === 'mine' && currentTeacherId) return s.teacherId === currentTeacherId;
      if (viewMode === 'all' && selectedTeacherId) return s.teacherId === selectedTeacherId;
      return true;
    });
    const sessionRows: RowDescriptor[] = sessionsScoped.map((session) => ({
      kind: 'session' as const,
      session,
      sortKey: { dayOfWeek: session.dayOfWeek, startTime: session.startTime },
    }));

    // Step 4: merge, apply the day-tab filter (uses sortKey.dayOfWeek so it
    // matches both row types), and sort.
    const merged = [...lessonRows, ...sessionRows];
    const filtered =
      selectedDay === 'all'
        ? merged
        : merged.filter((r) => r.sortKey.dayOfWeek === selectedDay);

    return filtered.sort((a, b) => {
      if (a.sortKey.dayOfWeek !== b.sortKey.dayOfWeek) {
        return a.sortKey.dayOfWeek - b.sortKey.dayOfWeek;
      }
      return a.sortKey.startTime.localeCompare(b.sortKey.startTime);
    });
  }, [
    allLessons,
    weekRange,
    hybridSessions,
    selectedDay,
    viewMode,
    currentTeacherId,
    selectedTeacherId,
    // The day-tab sort/filter now reads the occurrence's weekday, so this memo
    // must recompute when occurrences arrive — otherwise a rescheduled class
    // stays filed under its recurring day until some other dep happens to change.
    occurrenceByLessonId,
  ]);

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'mine' | 'all' | null) => {
    if (newMode) {
      setViewMode(newMode);
      if (newMode === 'mine') {
        setSelectedTeacherId('');
      }
    }
  };

  const handleTeacherChange = (event: SelectChangeEvent) => {
    setSelectedTeacherId(event.target.value);
  };

  return (
    <Box>
      <PageHeader
        title="Teacher Tasks"
        subtitle="Mark attendance, add notes, and track piece progress for your classes"
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon color="action" />
              <Typography variant="subtitle2" color="text.secondary">
                View:
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="mine">
                <PersonIcon sx={{ mr: 0.5 }} fontSize="small" />
                My Classes
              </ToggleButton>
              <ToggleButton value="all">
                <SchoolIcon sx={{ mr: 0.5 }} fontSize="small" />
                All School
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Teacher filter (only when viewing all) */}
            {viewMode === 'all' && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Teacher</InputLabel>
                <Select
                  value={selectedTeacherId}
                  onChange={handleTeacherChange}
                  label="Filter by Teacher"
                >
                  <MenuItem value="">All Teachers</MenuItem>
                  {teachers?.filter((t) => t.isActive).map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.User.firstName} {teacher.User.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={hideCompleted}
                  onChange={(e) => setHideCompleted(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VisibilityOffIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">Hide completed</Typography>
                </Box>
              }
              sx={{ ml: 'auto' }}
            />
          </Box>

          {/* Day Tabs */}
          <Tabs
            value={selectedDay}
            onChange={(_, v) => setSelectedDay(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="all" label="All Days" />
            {dayNames.map((day, index) => (
              <Tab key={day} value={index} label={day} />
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Lessons List */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {viewMode === 'mine' ? 'My Classes' : 'School Classes'}
            </Typography>
            <Chip
              label={`${filteredRows.length} class${filteredRows.length !== 1 ? 'es' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          {viewMode === 'all' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can mark attendance for any class (for coverage purposes).
            </Alert>
          )}

          <Divider sx={{ mb: 2 }} />

          {isLoading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : filteredRows.length === 0 ? (
            <Alert severity="info">
              {viewMode === 'mine'
                ? 'No classes assigned to you.'
                : 'No classes found matching your filters.'}
            </Alert>
          ) : (
            <List disablePadding>
              {filteredRows.map((row) =>
                row.kind === 'lesson' ? (
                  <LessonRow
                    key={`lesson-${row.lesson.id}`}
                    lesson={row.lesson}
                    viewMode={viewMode}
                    hideCompleted={hideCompleted}
                    occurrence={occurrenceByLessonId.get(row.lesson.id)}
                  />
                ) : (
                  <HybridSessionRow
                    key={`session-${row.session.bookingId}`}
                    session={row.session}
                    viewMode={viewMode}
                    hideCompleted={hideCompleted}
                  />
                )
              )}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
